import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import fallbackImage from '@/core/assets/img/background-default_grey.png';
import { BdsAvatar, BdsIcon } from '@/core/constants/webComponents';
import { Post } from '@/core/services/interfaces/PostInterfaces';
import { useValidatedThumbnail } from '@/core/hooks/useValidatedThumbnail';
import './FullClassifyModal.scss';
import { resolveImageUrl } from './ClassificationCard/types';
import { parseImageText } from '@/core/utils/parseImageText';
import { toSafeMarkdownHtml } from '@/core/utils/toSafeMarkdownHtml';

interface FullClassifyModalProps {
  post: Post;
  onClose: () => void;
}

export const FullClassifyModal: React.FC<FullClassifyModalProps> = ({ post, onClose }) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const avatarThumbnail = useValidatedThumbnail(post.post_user_image_url);
  const imageUrl = resolveImageUrl(post);
  const [imageSrc, setImageSrc] = useState(imageUrl);
  const renderedTextHtml = toSafeMarkdownHtml(post.text ?? '');

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || dialog.open) return;
    dialog.showModal();
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleBackdropClick = (e: MouseEvent) => {
      if (e.target === dialog) onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    dialog.addEventListener('click', handleBackdropClick);
    dialog.addEventListener('keydown', handleKeyDown);
    return () => {
      dialog.removeEventListener('click', handleBackdropClick);
      dialog.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    document.body.classList.add('full-classify-modal-open');
    return () => document.body.classList.remove('full-classify-modal-open');
  }, []);

  return (
    <dialog ref={dialogRef} className="full-classify-modal__overlay">
      <div className="full-classify-modal">
        {/* Close button */}
        <button
          className="full-classify-modal__close"
          onClick={onClose}
          aria-label={t('classificationItemModal.close')}
        >
          <BdsIcon name="close" size="small" />
        </button>

        {/* Header: avatar + name */}
        <div className="full-classify-modal__header">
          <div className="full-classify-modal__avatar-wrapper">
            <BdsAvatar
              class="full-classify-modal__avatar"
              size="standard"
              name={post.name}
              thumbnail={avatarThumbnail}
            />
          </div>
          <p className="full-classify-modal__author-name">{post.name}</p>
        </div>

        {/* Image */}
        {imageUrl && (
          <div className="full-classify-modal__image-wrapper">
            <img
              src={imageSrc}
              alt={post.name}
              className="full-classify-modal__image"
              onError={() => setImageSrc(fallbackImage)}
            />
          </div>
        )}

        {/* Text */}
        <p className="full-classify-modal__text" dangerouslySetInnerHTML={{ __html: renderedTextHtml }} />
        {post.computer_vision?.text && (
          <p
            className="full-classify-modal__text"
            dangerouslySetInnerHTML={{ __html: parseImageText(post.computer_vision, t('cardPosts.computerVisionHeader')) }}
          />
        )}

        {/* Link */}
        {post.post_url && (
          <a
            href={post.post_url}
            className="full-classify-modal__link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {post.post_url}
          </a>
        )}
      </div>
    </dialog>
  );
};
